import React from 'react';
import { Book, Package, Stethoscope, Users, Home, Calendar } from 'lucide-react';

const ServiceCard = ({ icon: Icon, title, description, details }) => (
    <div className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300">
        <div className="flex items-center justify-center w-12 h-12 mb-4 rounded-full bg-blue-100">
            <Icon className="h-6 w-6 text-blue-600" />
        </div>
        <h3 className="text-xl font-semibold mb-2">{title}</h3>
        <p className="text-gray-600 mb-4">{description}</p>
        {details && (
            <ul className="list-disc list-inside text-gray-600 space-y-2">
                {details.map((detail, index) => (
                    <li key={index}>{detail}</li>
                ))}
            </ul>
        )}
    </div>
);

const ServicesPage = () => {
    const services = [
        {
            icon: Package,
            title: "Monthly Distribution",
            description: "Regular distribution of essential items to families in need.",
            details: [
                "Food packages",
                "Clothing and shoes",
                "Personal hygiene products",
                "Distribution on the last Saturday of every month"
            ]
        },
        {
            icon: Book,
            title: "Educational Support",
            description: "Supporting students with necessary school materials.",
            details: [
                "School supplies distribution",
                "Annual back-to-school event",
                "Educational resources",
                "Student support programs"
            ]
        },
        {
            icon: Stethoscope,
            title: "Medical Assistance",
            description: "Financial support for medical needs and healthcare services.",
            details: [
                "Medical bill assistance",
                "Healthcare support",
                "Emergency medical aid",
                "Health education resources"
            ]
        },
        {
            icon: Home,
            title: "Housing Support",
            description: "Assistance with housing improvements and basic repairs.",
            details: [
                "Home repair assistance",
                "Housing improvements",
                "Emergency shelter support",
                "Living conditions enhancement"
            ]
        },
        {
            icon: Users,
            title: "Community Counseling",
            description: "Guidance and support for community members.",
            details: [
                "Personal counseling",
                "Family support services",
                "Youth mentoring",
                "Community outreach"
            ]
        },
        {
            icon: Calendar,
            title: "Special Events",
            description: "Regular community events and special programs.",
            details: [
                "Holiday celebrations",
                "Community gatherings",
                "Fundraising events",
                "Volunteer opportunities"
            ]
        }
    ];

    return (
        <div className="flex flex-col min-h-screen">
            {/* Hero Section */}
            <section className="bg-blue-600 text-white py-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center">
                        <h1 className="text-4xl md:text-5xl font-bold mb-6">Our Services</h1>
                        <p className="text-xl mb-4 max-w-3xl mx-auto">
                            Our satisfaction comes from helping those in need. We provide various
                            services to support our community members.
                        </p>
                    </div>
                </div>
            </section>

            {/* Services Grid */}
            <section className="py-16 bg-gray-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {services.map((service, index) => (
                            <ServiceCard key={index} {...service} />
                        ))}
                    </div>
                </div>
            </section>

            {/* Impact Statistics */}
            <section className="py-16 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-bold text-center mb-12">Our Impact</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                        <div className="p-6">
                            <div className="text-4xl font-bold text-blue-600 mb-2">12+</div>
                            <div className="text-gray-600">Families Supported Monthly</div>
                        </div>
                        <div className="p-6">
                            <div className="text-4xl font-bold text-blue-600 mb-2">100+</div>
                            <div className="text-gray-600">Children Helped Annually</div>
                        </div>
                        <div className="p-6">
                            <div className="text-4xl font-bold text-blue-600 mb-2">15+</div>
                            <div className="text-gray-600">Years of Service</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Call to Action */}
            <section className="bg-blue-600 text-white py-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl font-bold mb-4">Need Assistance?</h2>
                    <p className="text-xl mb-8">
                        Contact us to learn more about our services and how we can help you.
                    </p>
                    <div className="space-x-4">
                        <a
                            href="/contact"
                            className="bg-white text-blue-600 px-8 py-3 rounded-md font-semibold
                       hover:bg-blue-50 transition-colors duration-300 inline-block"
                        >
                            Contact Us
                        </a>
                        <a
                            href="/support"
                            className="border-2 border-white text-white px-8 py-3 rounded-md
                       font-semibold hover:bg-blue-700 transition-colors duration-300 inline-block"
                        >
                            Support Our Mission
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ServicesPage;